import { useState } from "react";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { HStack } from "@design-system/HStack/HStack";
import { Modal, StandardModalHeader } from "@design-system/Modal/Modal";
import { VStack } from "@design-system/VStack/VStack";
import { EntityField } from "./AssignmentFields";
import {
    CLIENT_EDITABLE_UNTIL_ENTITY_HELPER,
    entityAssignmentModalTitle,
} from "./assignmentCopy";
import { useOrganizationIntegrations } from "./OrganizationIntegrationsContext";
import {
    assignmentCapReason,
    useCommitAssignments,
} from "./useCommitAssignments";
import styles from "./IntegrationsPage.module.scss";

interface EntityAssignmentModalProps {
    /** The accounts this confirm applies to — one row, or a whole selection. */
    accountIds: number[];
    /**
     * The client the accounts sit under, either because they are assigned into
     * it or because it was staged for them. Entity options are scoped to it.
     */
    workspaceId: string;
    /**
     * The entity to start on, for accounts that already have one. Left unset
     * for a selection whose rows don't share a single current entity.
     */
    initialEntityId?: number | null;
    onClose: () => void;
    /** Receives the accounts that actually persisted, if any did. */
    onAssigned?: (accountIds: number[]) => void;
}

/**
 * Assigns accounts to an entity of a client that is already settled — the only
 * half of the assignment that reaches the server, since the API assigns by
 * `entityId`.
 *
 * Mounted only while open, so every opening starts from the row (or selection)
 * it was opened for rather than whatever was picked last time.
 */
export const EntityAssignmentModal = ({
    accountIds,
    workspaceId,
    initialEntityId = null,
    onClose,
    onAssigned,
}: EntityAssignmentModalProps) => {
    const { workspaces, assignmentMode, pendingByAccountId, setPendingEntity } =
        useOrganizationIntegrations();
    const { commit, isCommitting } = useCommitAssignments();
    const [entityId, setEntityId] = useState<number | null>(initialEntityId);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const workspace = workspaces.find(
        (candidate) => candidate.id === workspaceId,
    );

    // The client is only staged while it has no entity to be persisted with,
    // so a staged account is one whose client is still open to change. An
    // account that arrives here already assigned has nothing to say about its
    // client — that one is settled, and the line would read as an offer the
    // modal can't honour.
    const isClientUnsaved = accountIds.some(
        (accountId) => pendingByAccountId[accountId] !== undefined,
    );

    const handleDismiss = () => {
        // Dismissing mid-request would unmount the mutation's observer and lose
        // the outcome, so the modal holds until the request settles.
        if (isSubmitting) {
            return;
        }
        onClose();
    };

    const isInFlight = isSubmitting || isCommitting;
    const blockedReason = assignmentCapReason(accountIds.length);

    const handleConfirm = () => {
        if (entityId === null || blockedReason !== undefined) {
            return;
        }
        // Staged surfaces send every pick together when the user confirms
        // there, so this one only lands in staging and the modal closes.
        if (assignmentMode === "stage") {
            setPendingEntity({ accountIds, workspaceId, entityId });
            onClose();
            return;
        }
        setIsSubmitting(true);
        commit(
            accountIds.map((accountId) => ({
                financialAccountId: accountId,
                entityId,
            })),
            { onAssigned, onSettled: onClose },
        );
    };

    return (
        <Modal
            show
            variant="plain"
            dialogClassName={styles.assignmentDialog}
            onClose={handleDismiss}
        >
            <StandardModalHeader>
                {entityAssignmentModalTitle(accountIds.length)}
            </StandardModalHeader>
            <VStack gap={24}>
                <VStack gap={16}>
                    {isClientUnsaved && (
                        <span className={styles.confirmHelper}>
                            {CLIENT_EDITABLE_UNTIL_ENTITY_HELPER}
                        </span>
                    )}
                    <EntityField
                        workspace={workspace}
                        entityId={entityId}
                        onChange={setEntityId}
                    />
                </VStack>
                <HStack justify="end">
                    <WithHotkey
                        hotKey="mod+enter"
                        hotKeyDisplayPosition="inline"
                        tooltip={blockedReason}
                        hotKeyOptions={{
                            description: "Confirm entity assignment",
                            preventDefault: true,
                        }}
                    >
                        {(hotkeyHint) => (
                            <Button
                                variant="primary"
                                size="md"
                                onClick={handleConfirm}
                                disabled={
                                    entityId === null ||
                                    isInFlight ||
                                    blockedReason !== undefined
                                }
                                rightIcon={hotkeyHint}
                            >
                                {isInFlight ? "Confirming..." : "Confirm"}
                            </Button>
                        )}
                    </WithHotkey>
                </HStack>
            </VStack>
        </Modal>
    );
};
